import DOMPurify from 'dompurify';
import { useEffect, useRef, useState } from 'react';
import { Check, ExternalLink, MousePointer2, Plus, Scissors, X } from 'lucide-react';

function formatDate(value) {
  if (!value) return 'Kein Datum';
  return new Intl.DateTimeFormat('de-DE', { dateStyle: 'full', timeStyle: 'short' }).format(new Date(value));
}

function selectionToHtml(range) {
  const fragment = range.cloneContents();
  const wrapper = document.createElement('div');
  wrapper.appendChild(fragment);
  return wrapper.innerHTML;
}

function defaultTitle(text) {
  return (
    text
      .split('\n')
      .map((line) => line.trim())
      .find(Boolean)
      ?.slice(0, 120) || ''
  );
}

function excerpt(text, max = 96) {
  if (!text) return '';
  const normalized = String(text).replace(/\s+/g, ' ').trim();
  return normalized.length > max ? `${normalized.slice(0, max - 1)}...` : normalized;
}

export default function ArticleDetailModal({
  article,
  loading,
  onClose,
  onExtract,
  onOpenCollection,
  onStatus,
  selectedArticles = [],
}) {
  const contentRef = useRef(null);
  const [selection, setSelection] = useState({ text: '', html: '' });
  const [extractTitle, setExtractTitle] = useState('');
  const [extractSummary, setExtractSummary] = useState('');
  const [titleTouched, setTitleTouched] = useState(false);
  const [summaryTouched, setSummaryTouched] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extractMessage, setExtractMessage] = useState('');
  const [extractError, setExtractError] = useState('');
  const isNewsletter = article?.source_type === 'newsletter' || (article?.tags || []).includes('newsletter');
  const selectedCount = selectedArticles.length;

  useEffect(() => {
    setSelection({ text: '', html: '' });
    setExtractTitle('');
    setExtractSummary('');
    setTitleTouched(false);
    setSummaryTouched(false);
    setExtractMessage('');
    setExtractError('');
  }, [article?.id]);

  useEffect(() => {
    if (!isNewsletter) return undefined;
    let timeoutId;
    const captureSoon = () => {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(captureSelection, 0);
    };

    document.addEventListener('mouseup', captureSoon);
    document.addEventListener('keyup', captureSelection);
    document.addEventListener('copy', captureSelection);

    return () => {
      window.clearTimeout(timeoutId);
      document.removeEventListener('mouseup', captureSoon);
      document.removeEventListener('keyup', captureSelection);
      document.removeEventListener('copy', captureSelection);
    };
  }, [isNewsletter, article?.id, titleTouched, summaryTouched]);

  if (!article && !loading) return null;

  const hasSelection = selection.text.trim().length >= 20;

  function captureSelection() {
    if (!contentRef.current) return;
    const activeSelection = window.getSelection();
    if (!activeSelection || activeSelection.rangeCount === 0 || activeSelection.isCollapsed) return;

    const range = activeSelection.getRangeAt(0);
    if (
      !contentRef.current.contains(range.commonAncestorContainer) &&
      !contentRef.current.contains(range.startContainer) &&
      !contentRef.current.contains(range.endContainer)
    ) {
      return;
    }

    const text = activeSelection.toString().trim();
    if (!text) return;

    const html = DOMPurify.sanitize(selectionToHtml(range));
    setSelection({ text, html });
    if (!titleTouched) setExtractTitle(defaultTitle(text));
    if (!summaryTouched) setExtractSummary(text.replace(/\s+/g, ' ').slice(0, 240));
    setExtractMessage('');
    setExtractError('');
  }

  async function submitExtract() {
    if (!hasSelection || !onExtract) return;
    setExtracting(true);
    setExtractError('');
    setExtractMessage('');
    try {
      const created = await onExtract(article.id, {
        title: extractTitle,
        summary: extractSummary,
        selected_text: selection.text,
        selected_html: selection.html,
      });
      setExtractMessage(`Ausschnitt wurde erstellt und fuer die Collection ausgewaehlt: ${created.title}`);
      setSelection({ text: '', html: '' });
      setExtractTitle('');
      setExtractSummary('');
      setTitleTouched(false);
      setSummaryTouched(false);
    } catch (err) {
      setExtractError(err.message || 'Ausschnitt konnte nicht erstellt werden.');
    } finally {
      setExtracting(false);
    }
  }

  function keepSelection(event) {
    event.preventDefault();
    captureSelection();
  }

  async function removeSelected(articleId) {
    await onStatus(articleId, 'new');
  }

  const content = article ? (
    <div className={isNewsletter ? 'content-box newsletter-content' : 'content-box'} onKeyUp={captureSelection} onMouseUp={captureSelection} ref={contentRef}>
      {article.content_html ? (
        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content_html) }} />
      ) : (
        <p>{article.summary}</p>
      )}
    </div>
  ) : null;

  const actions = article ? (
    <footer className="modal-actions">
      <button className="selected-button" onClick={() => onStatus(article.id, 'selected')} type="button">
        <Check size={16} />
        {isNewsletter ? 'Ganzen Newsletter auswaehlen' : 'Select for Collection'}
      </button>
      <button className="danger-button" onClick={() => onStatus(article.id, 'rejected')} type="button">
        <X size={16} />
        Reject
      </button>
      <button className="secondary-button" onClick={onClose} type="button">
        Close
      </button>
    </footer>
  ) : null;

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true">
      <section className="detail-modal">
        <header className="modal-header">
          <div>
            <span className="eyebrow">{isNewsletter ? 'Newsletter durchsuchen' : 'Article Details'}</span>
            <h2>{loading ? 'Laden...' : article.title}</h2>
          </div>
          <button className="icon-button" onClick={onClose} type="button" title="Schliessen">
            <X size={18} />
          </button>
        </header>

        {!loading && article ? (
          <>
            <dl className="detail-grid">
              <div>
                <dt>Source</dt>
                <dd>{article.source_name}</dd>
              </div>
              <div>
                <dt>Region</dt>
                <dd>{article.region || '-'}</dd>
              </div>
              <div>
                <dt>Date</dt>
                <dd>{formatDate(article.published_at || article.created_at)}</dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>{article.status}</dd>
              </div>
            </dl>

            {article.url ? (
              <a className="source-link" href={article.url} target="_blank" rel="noreferrer">
                <ExternalLink size={16} />
                Quelle oeffnen
              </a>
            ) : null}

            {isNewsletter ? (
              <div className="newsletter-workspace">
                <div className="newsletter-reader">{content}</div>
                <aside className="newsletter-sidebar">
                  <section className="extract-panel">
                    <div className="panel-title-row">
                      <span className="eyebrow">Ausschnitt</span>
                      <span>{selection.text ? `${selection.text.length} Zeichen` : 'Keine Markierung'}</span>
                    </div>
                    <div className="selection-preview">{selection.text ? excerpt(selection.text, 360) : 'Noch kein Ausschnitt markiert.'}</div>
                    <button className="secondary-button" disabled={extracting} onMouseDown={keepSelection} type="button">
                      <MousePointer2 size={16} />
                      Markierung uebernehmen
                    </button>
                    <input
                      disabled={!hasSelection || extracting}
                      onChange={(event) => {
                        setTitleTouched(true);
                        setExtractTitle(event.target.value);
                      }}
                      placeholder="Titel des Ausschnitts"
                      value={extractTitle}
                    />
                    <textarea
                      disabled={!hasSelection || extracting}
                      onChange={(event) => {
                        setSummaryTouched(true);
                        setExtractSummary(event.target.value);
                      }}
                      placeholder="Kurzbeschreibung"
                      rows={3}
                      value={extractSummary}
                    />
                    <button className="primary-button" disabled={!hasSelection || extracting} onClick={submitExtract} type="button">
                      <Scissors size={16} />
                      {extracting ? 'Hinzufuegen...' : 'Zur Collection hinzufuegen'}
                    </button>
                    {extractMessage ? <div className="inline-success">{extractMessage}</div> : null}
                    {extractError ? <div className="inline-error">{extractError}</div> : null}
                  </section>

                  <section className="collection-basket">
                    <div className="panel-title-row">
                      <span className="eyebrow">Collection-Korb</span>
                      <span>{selectedCount}</span>
                    </div>
                    <div className="basket-items">
                      {selectedArticles.length ? (
                        selectedArticles.map((selectedArticle) => (
                          <div className="basket-item" key={selectedArticle.id}>
                            <p>{excerpt(selectedArticle.title, 88)}</p>
                            <button className="icon-button" onClick={() => removeSelected(selectedArticle.id)} title="Entfernen" type="button">
                              <X size={15} />
                            </button>
                          </div>
                        ))
                      ) : (
                        <div className="basket-empty">Noch keine Auswahl.</div>
                      )}
                    </div>
                    <button className="primary-button" disabled={!selectedCount} onClick={onOpenCollection} type="button">
                      <Plus size={16} />
                      Collection erstellen
                    </button>
                  </section>
                </aside>
              </div>
            ) : (
              content
            )}

            <div className="metadata-row">
              <span>Hash: {article.content_hash || '-'}</span>
              <span>Fetched: {formatDate(article.created_at)}</span>
              <span>Tags: {(article.tags || []).join(', ') || '-'}</span>
            </div>

            {actions}
          </>
        ) : null}
      </section>
    </div>
  );
}
